<script setup>
import { ref, computed, watch } from 'vue';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import { Link, Head, usePage } from '@inertiajs/vue3';
import { Dialog, DialogPanel, Disclosure, DisclosureButton, DialogTitle, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems, Popover, PopoverButton, PopoverGroup, PopoverPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import Paginator from 'primevue/paginator';

const props = defineProps({ favorites: Array, categories: Array, brands: Array });
const page = usePage();

let eurFormatter = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const pageFavorites = computed(() => page.props.favorites);

watch(pageFavorites, () => {
    favorites.value = pageFavorites.value;
    favorites.value = doFilter();
    //set paginator to page 1
    if (paginator.value) {
        paginator.value.changePage(0);
    }
})

const favorites = ref(props.favorites);
const paginator = ref(null);
const paginatedData = ref([]);
const searchValue = ref('');

watch(searchValue, (newValue) => {
    favorites.value = doFilter();
});

watch(favorites, () => {
    if (paginator.value) {
        paginator.value.changePage(0);
    }
    changePagination({ rows: 10, page: 0 });
});

function changePagination(pageState) {
    //get row count
    const rows = pageState.rows;
    //get page count
    const page = pageState.page;

    //calculate start index
    const startIndex = rows * page;
    //calculate end index
    const endIndex = startIndex + rows;

    //get the data for the current page
    paginatedData.value = favorites.value.slice(startIndex, endIndex);
}

changePagination({ rows: 10, page: 0 });

function doFilter() {
    const filteredLots = pageFavorites.value.filter(lot => {
        if (activePriceFilters.value.length > 0) {
            // const price = parseInt(lot.price)
            //get the highest bid price
            const price = lot.bids.length > 0 ? lot.bids[0].amount : 0;
            if (activePriceFilters.value.includes('to10k') && price > 10000) return false
            if (activePriceFilters.value.includes('to20k') && price > 20000) return false
            if (activePriceFilters.value.includes('to30k') && price > 30000) return false
            if (activePriceFilters.value.includes('to40k') && price > 40000) return false
            if (activePriceFilters.value.includes('to50k') && price > 50000) return false
            if (activePriceFilters.value.includes('over50k') && price < 50000) return false
        }

        // if (activeStatusFilters.value.length > 0) {
        //     if (!activeStatusFilters.value.includes(lot.status)) return false
        // }

        if (activeCategoryFilters.value.length > 0) {
            if (!activeCategoryFilters.value.includes(lot.category_id)) return false
        }

        if (activeBrandFilters.value.length > 0) {
            //check if brand is in active filters, ignore uppercase
            if (!activeBrandFilters.value.includes(lot.make.toLowerCase())) return false

        }

        if (activeMileageFilters.value.length > 0) {
            if (activeMileageFilters.value.includes('to100') && lot.mileage > 100000) return false
            if (activeMileageFilters.value.includes('to200') && lot.mileage > 200000) return false
            if (activeMileageFilters.value.includes('over200') && lot.mileage < 200000) return false
        }

        if (activeYearFilters.value.length > 0) {
            if (!activeYearFilters.value.includes(parseInt(lot.construction_year))) return false
        }

        if (activeTransmissionFilters.value.length > 0) {
            if (!activeTransmissionFilters.value.includes(lot.transmission)) return false
        }

        if (activeFuelFilters.value.length > 0) {
            if (!activeFuelFilters.value.includes(lot.fuel_type)) return false
        }

        if (activeColorFilters.value.length > 0) {
            if (!activeColorFilters.value.includes(lot.color)) return false
        }

        return true
    });

    if (searchValue.value) {
        return filteredLots.filter(lot => {
            return lot.make.toLowerCase().includes(searchValue.value.toLowerCase()) || lot.model.toLowerCase().includes(searchValue.value.toLowerCase())
        });
    }

    return filteredLots;
}

function formatMileage(mileage) {
    return mileage.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")
}

const deleteDialog = ref(false);

let deleteDialogText = "";
let deleteDialogUrl = "";

function openDeleteDialog(item) {
  deleteDialogText = item.name;
  deleteDialogUrl = "/portal/kavel/" + item.id + "/unfavorite";
  deleteDialog.value = true;
}

function getCurrentSortOption() {
    //get url sort parameter
    let urlParams = new URLSearchParams(window.location.search);
    let sort = urlParams.get('sort');
    if (sort) {
        let sortOption = sortOptions.find(option => option.href.includes(sort));
        return sortOption.name;
    } else {
        return 'Nieuwste';
    }
}

const activeCategoryFilters = ref([])
const activeBrandFilters = ref([])
const activeMileageFilters = ref([])
const activeYearFilters = ref([])
const activeTransmissionFilters = ref([])
const activeFuelFilters = ref([])
const activeColorFilters = ref([])
const activePriceFilters = ref([])

function getModel(sectionId) {
    switch (sectionId) {
        case 'category':
            return activeCategoryFilters
        case 'merk':
            return activeBrandFilters
        case 'mileage':
            return activeMileageFilters
        case 'year':
            return activeYearFilters
        case 'transmission':
            return activeTransmissionFilters
        case 'fuel':
            return activeFuelFilters
        case 'color':
            return activeColorFilters
        case 'price':
            return activePriceFilters
    }
}

watch([activeCategoryFilters, activeBrandFilters, activeMileageFilters, activeYearFilters, activeTransmissionFilters, activeFuelFilters, activeColorFilters, activePriceFilters], () => {
    //filter favorites
    favorites.value = doFilter();

    changePagination({ rows: 10, page: 0 });
})

const sortOptions = [
    { name: 'Nieuwste', href: '?sort=created-desc' },
    { name: 'Oudste', href: '?sort=created-asc' },
    { name: 'Prijs: Hoog tot laag', href: '?sort=price-desc' },
    { name: 'Prijs: Laag tot hoog', href: '?sort=price-asc' },
]

const filters = [
    {
        id: 'category',
        name: 'Categorie',
        options: props.categories.map(category => ({ value: category.id, label: category.name })),
    },
    {
        id: 'merk',
        name: 'Merk',
        options: props.brands.map(brand => ({ value: brand.name.toLowerCase(), label: brand.name })),
    },
    {
        id: 'mileage',
        name: 'KM-stand',
        options: [
            { value: 'to100', label: 'Tot 100.000km' },
            { value: 'to200', label: 'Tot 200.000km' },
            { value: 'over200', label: "Over 200.000km" },
        ],
    },
    {
        id: 'year',
        name: 'Bouwjaar',
        options: Array.from({ length: 50 }, (_, i) => {
            const year = new Date().getFullYear() - i
            return { value: year, label: year }
        }),
    },
    {
        id: 'transmission',
        name: 'Transmissie',
        options: [
            { value: 'handgeschakeld', label: 'Handmatig' },
            { value: 'semi-automaat', label: 'Semi-automaat' },
            { value: 'automaat', label: 'Automaat' },
        ],
    },
    {
        id: 'fuel',
        name: 'Brandstof',
        options: [
            { value: 'benzine', label: 'Benzine' },
            { value: 'diesel', label: 'Diesel' },
            { value: 'benzine-elektriciteit', label: 'Hybride benzine' },
            { value: 'diesel-elektriciteit', label: 'Hybride diesel' },
            { value: 'elektriciteit', label: 'Elektrisch' },
            { value: 'lpg', label: 'LPG' },
        ],
    },
    {
        id: 'color',
        name: 'Kleur',
        options: [
            { label: "Rood", value: "rood" },
            { label: "Blauw", value: "blauw" },
            { label: "Groen", value: "groen" },
            { label: "Zwart", value: "zwart" },
            { label: "Wit", value: "wit" },
            { label: "Grijs", value: "grijs" },
            { label: "Geel", value: "geel" },
            { label: "Oranje", value: "oranje" },
            { label: "Paars", value: "paars" },
            { label: "Roze", value: "roze" },
            { label: "Bruin", value: "bruin" },
            { label: "Beige", value: "beige" },
            { label: "Turquoise", value: "turquoise" },
            { label: "Zilver", value: "zilver" },
            { label: "Goud", value: "goud" },
            { label: "Chroom", value: "chroom" },
            { label: "Bronze", value: "bronze" },
            { label: "Overig", value: "overig" },
        ],
    },
    {
        id: 'price',
        name: 'Hoogste bod',
        options: [
            { value: 'to10k', label: 'Tot €10.000' },
            { value: 'to20k', label: 'Tot €20.000' },
            { value: 'to30k', label: 'Tot €30.000' },
            { value: 'to40k', label: 'Tot €40.000' },
            { value: 'to50k', label: 'Tot €50.000' },
            { value: 'over50k', label: 'Over €50.000' },
        ],
    },
]

const mobileFiltersOpen = ref(false)

</script>

<template>
  <Head title="Favoriete kavels | Mijnautosnelweg" />
  <main class="px-4 sm:px-6 py-6 space-y-8">
    <div class="">
      <nav class="sm:hidden" aria-label="Back">
        <Link href="/portal/dashboard" class="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 gap-x-1">
            <font-awesome-icon :icon="['fas', 'chevron-left']" class="h-3 w-3 text-gray-400 shrink-0" />
            Terug
        </Link>
      </nav>
      <nav class="hidden sm:flex" aria-label="Breadcrumb">
        <ol role="list" class="flex items-center space-x-4">
          <li>
            <div class="flex">
              <Link href="/portal/dashboard" class="text-sm font-medium text-gray-500 hover:text-gray-700">Dashboard
              </Link>
            </div>
          </li>
          <font-awesome-icon :icon="['fas', 'chevron-right']" class="h-3 w-3 text-gray-400 shrink-0" />
          <li>
            <div class="flex items-center">
              <Link href="#" class="text-sm font-medium text-gray-900 hover:text-gray-700">
              Favorieten</Link>
            </div>
          </li>
        </ol>
      </nav>
    </div>
    <div class="flex flex-col sm:flex-row justify-between w-full gap-y-2">
      <div class="flex flex-col">
        <h1 class="text-base font-semibold leading-6 text-gray-900">Jouw favoriete kavels</h1>
        <p class="mt-2 text-sm text-gray-700">Een overzicht van je favoriete kavels.</p>
      </div>
      <div class="flex flex-row gap-3 items-center" v-if="favorites.length > 0">
        <IconField iconPosition="right" class="w-full sm:w-auto">
          <InputIcon class="pi pi-search"></InputIcon>
          <InputText class="h-10" placeholder="Search" v-model="searchValue" />
        </IconField>
        <Link href="/portal/aanbod" class="border p-2 rounded-lg cursor-pointer">
            <font-awesome-icon :icon="['far', 'car']" class="h-6 w-6 text-royalblue-500 shrink-0" aria-hidden="true" />
        </Link>
      </div>
    </div>
    <div>
      <div>
            <!-- Mobile filter dialog -->
            <TransitionRoot as="template" :show="mobileFiltersOpen">
                <Dialog class="relative z-40 xl:hidden" @close="mobileFiltersOpen = false">
                    <TransitionChild as="template" enter="transition-opacity ease-linear duration-300"
                        enter-from="opacity-0" enter-to="opacity-100"
                        leave="transition-opacity ease-linear duration-300" leave-from="opacity-100"
                        leave-to="opacity-0">
                        <div class="fixed left-0 right-0 top-0 bottom-0 bg-black bg-opacity-5" />
                    </TransitionChild>
                    <div class="fixed left-0 right-0 top-0 bottom-0 z-40 flex">
                        <TransitionChild as="template" enter="transition ease-in-out duration-300 transform"
                            enter-from="translate-x-full" enter-to="translate-x-0"
                            leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0"
                            leave-to="translate-x-full">
                            <DialogPanel
                                class="relative ml-auto flex h-full w-full max-w-xs bg-white flex-col overflow-y-auto  py-4 pb-6 shadow-xl">
                                <div class="flex items-center justify-between px-4">
                                    <h2 class="text-lg font-medium text-gray-900">Filters</h2>
                                    <button type="button"
                                        class="-mr-2 flex h-10 w-10 items-center justify-center rounded-md  p-2 text-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brightorange"
                                        @click="mobileFiltersOpen = false">
                                        <span class="sr-only">Close menu</span><font-awesome-icon :icon="['far', 'xmark']" class="h-6 w-6 shrink-0"/>

                                    </button>
                                </div>

                                <!-- Filters -->
                                <form class="mt-4">
                                    <Disclosure as="div" v-for="section in filters" :key="section.name"
                                        class="border-t border-gray-200 px-4 py-6" v-slot="{ open }">
                                        <h3 class="-mx-2 -my-3 flow-root">
                                            <DisclosureButton
                                                class="flex w-full items-center justify-between  px-2 py-3 text-sm text-gray-400">
                                                <span class="font-medium text-gray-900">{{ section.name }}</span>
                                                <span class="ml-6 flex items-center">
                                                    <font-awesome-icon :icon="['far', 'chevron-down']" rotation="90" :class="{ 'h-4 w-4 text-royalblue-500 fa-rotate': open, 'hidden': !open }" aria-hidden="true" />
                                                    <font-awesome-icon :icon="['far', 'chevron-right']" rotation="180" :class="{ 'hidden': open, 'h-4 w-4 text-royalblue-500': !open }" aria-hidden="true" />
                                                </span>
                                            </DisclosureButton>
                                        </h3>
                                        <DisclosurePanel class="pt-6">
                                            <div class="space-y-2">
                                                <div v-for="(option, optionIdx) in section.options" :key="option.value"
                                                    class="flex items-center">
                                                    <input :id="`filter-mobile-${section.id}-${optionIdx}`" v-model="getModel(section.id).value"
                                                    :value="option.value" :checked="getModel(section.id).value.includes(option.value)" type="checkbox" 
                                                        class="h-4 w-4 ring-1 ring-inset ring-gray-300 rounded border-gray-300 text-brightorange focus:ring-brightorange" />
                                                    <label :for="`filter-mobile-${section.id}-${optionIdx}`"
                                                        class="ml-3 text-sm text-gray-500">{{ option.label }}</label>
                                                </div>
                                            </div>
                                        </DisclosurePanel>
                                    </Disclosure>
                                </form>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </Dialog>
            </TransitionRoot>

            <!-- Filters -->
            <section aria-labelledby="filter-heading">
                <h2 id="filter-heading" class="sr-only">Product filters</h2>

                <div class="flex items-center justify-between">

                    <button type="button"
                        class="ring-1 ring-inset ring-gray-300 rounded-lg py-3.5 px-2.5 flex flex-row gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 xl:hidden"
                        @click="mobileFiltersOpen = true">
                        <font-awesome-icon :icon="['far', 'filter']" class="h-5 w-5 shrink-0"/>

                        Filters
                    </button>

                    <PopoverGroup class="hidden xl:flex md:items-baseline sm:space-x-8">
                        <Popover as="div" v-for="(section, sectionIdx) in filters" :key="section.name" id="menu"
                            class="relative inline-block text-left">
                            <PopoverButton
                                class="group inline-flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                                <span>{{ section.name }}</span>
                                <span v-if="getModel(section.id).value.length > 0"
                                    class="ml-1.5 rounded bg-gray-200 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-gray-700">{{getModel(section.id).value.length}}</span>
                                <font-awesome-icon :icon="['far', 'chevron-right']" rotation="90" class="h-4 w-4 text-royalblue-500" aria-hidden="true" />
                            </PopoverButton>

                            <transition enter-active-class="transition ease-out duration-100"
                                enter-from-class="transform opacity-0 scale-95"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-from-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95">
                                <PopoverPanel
                                    class="absolute max-h-64 overflow-scroll bg-white border z-10 mt-2 origin-top-right rounded-md  p-4 shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none">
                                    <form class="space-y-4">
                                        <div v-for="(option, optionIdx) in section.options" :key="option.value" 
                                            class="flex items-center">
                                            <input :id="`filter-${section.id}-${optionIdx}`" v-model="getModel(section.id).value"
                                                :value="option.value" :checked="getModel(section.id).value.includes(option.value)" type="checkbox" 
                                                class="h-4 w-4 ring-1 ring-inset ring-gray-300 rounded border-gray-300 text-brightorange focus:ring-brightorange" />
                                            <label :for="`filter-${section.id}-${optionIdx}`"
                                                class="ml-3 whitespace-nowrap pr-6 text-sm font-medium text-gray-900">{{
                                                option.label
                                                }}</label>
                                        </div>
                                    </form>
                                </PopoverPanel>
                            </transition>
                        </Popover>
                    </PopoverGroup>


                    <Menu as="div" class="relative inline-block text-left">
                        <div class="flex flex-row gap-2 items-center">

                            <MenuButton
                                class="group inline-flex justify-center items-center text-sm font-medium text-gray-700 hover:text-gray-900">
                                {{getCurrentSortOption()}}
                                <font-awesome-icon :icon="['fal', 'sort']" class="h-4 w-4 text-royalblue-500" aria-hidden="true" />
                            </MenuButton>
                        </div>

                        <transition enter-active-class="transition ease-out duration-100"
                            enter-from-class="transform opacity-0 scale-95"
                            enter-to-class="transform opacity-100 scale-100"
                            leave-active-class="transition ease-in duration-75"
                            leave-from-class="transform opacity-100 scale-100"
                            leave-to-class="transform opacity-0 scale-95">
                            <MenuItems
                                class="absolute bg-white right-0 border z-10 mt-2 w-48 origin-top-left rounded-md  shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none">
                                <div class="py-1">
                                    <MenuItem v-for="option in sortOptions" :key="option"
                                        v-slot="{ sortOptionSelected }">
                                    <Link :href="option.href"
                                        :class="[sortOptionSelected ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm font-medium text-gray-900']">{{
                                        option.name
                                        }}</Link>
                                    </MenuItem>
                                </div>
                            </MenuItems>
                        </transition>
                    </Menu>
                </div>
            </section>
        </div>
      <div class="space-y-8">
        <div class="mt-10 ring-1 ring-gray-300 rounded-lg w-full" v-if="favorites.length > 0">
          <table class="min-w-full divide-y divide-gray-300 border-b">
            <thead>
                        <tr>
                            <th scope="col"
                                class="hidden sm:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Kavelnummer
                            </th>
                            <th scope="col" class="px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Categorie</th>
                            <th scope="col" class="px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Merk &
                                model</th>
                            <th scope="col" class="hidden md:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                KM-stand
                            </th>
                            <th scope="col"
                                class="hidden lg:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Bouwjaar
                            </th>
                            <th scope="col"
                                class="hidden xl:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Transmissie
                            </th>
                            <th scope="col" class="px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Hoogste bod
                            </th>
                            <th scope="col" class="hidden 2xl:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Verkoper
                            </th>
                        </tr>
                    </thead>
                    <tbody class="divide-y">
                        <tr v-for="auto in paginatedData" :key="auto.model" class="relative">
                            <td
                                class="hidden sm:table-cell whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                                {{
                                auto.id }}
                            </td>
                            <td
                                class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {{ auto.category ? auto.category.name : 'Geen categorie' }} 
                            </td>
                            <td
                                class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 max-w-1 overflow-clip overflow-ellipsis">
                                {{ auto.make }} {{ auto.model
                                }}
                                {{ auto.body_type ?? '' }}
                            </td>
                            <td class="hidden lg:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ formatMileage(auto.mileage) }} km
                            </td>
                            <td class="hidden md:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{
                                auto.construction_year }}
                            </td>
                            <td class="hidden xl:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{
                                auto.transmission }}</td>
                            <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ auto.highest_bid ? eurFormatter.format(auto.highest_bid) : 'Geen biedingen' }}</td>
                            <td class="hidden 2xl:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ auto.seller_id ? auto.seller.full_name : 'Geen verkoper' }}</td>

                            <td class="hidden lg:flex whitespace-nowrap py-4 pr-4 pl-3 text-right text-sm font-medium gap-1.5">
                                <Link :href="'/portal/kavel/' + auto.id"
                                    class="text-royalblue-500 hover:text-royalblue-500/80">
                                Bekijk
                                </Link>
                            </td>
                            <td class="flex lg:hidden whitespace-nowrap py-4 text-sm gap-1.5 pr-3">
                                <Link :href="'/portal/kavel/' + auto.id"
                                class="text-royalblue-500 hover:text-royalblue-500/80">
                                <font-awesome-icon :icon="['far', 'eye']" class="text-royalblue-500 hover:text-royalblue-500/80 h-5 w-5 shrink-0"/>
<span class="sr-only">,
                                {{
                                auto.id }}</span></Link>
                            </td>
                        </tr>
                    </tbody>
          </table>
        </div>
        <div v-else>
            <div class="text-center pt-5">              
                <font-awesome-icon :icon="['far', 'heart']" class="h-12 w12 mx-auto text-gray-400 shrink-0" />
                <h3 class="mt-4 text-sm font-semibold text-gray-900">Geen favorieten</h3>
                <p class="mt-1 text-sm text-gray-500">Bekijk het aanbod en zoek wat leuks uit!</p>
                <div class="mt-6">
                <Link href="/portal/aanbod" type="button"
                    class="inline-flex items-center rounded-md bg-royalblue-500 hover:bg-royalblue-500/80 px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">

                Naar aanbod
                </Link>
                </div>
            </div>
        </div>
        <Paginator ref="paginator" v-if="favorites.length > 10" @page="changePagination" :rows="10"
          :totalRecords="favorites.length" :rowsPerPageOptions="[10, 20, 30]"></Paginator>
      </div>
    </div>
  </main>

  <!-- DELETE DIALOG -->
  <TransitionRoot as="template" :show="deleteDialog">
    <Dialog as="div" class="relative z-40" @close="deleteDialog = false">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
        leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed left-0 right-0 top-0 bottom-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed left-0 right-0 top-0 bottom-0 z-40 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div class="sm:flex sm:items-start">
                <div
                  class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <font-awesome-icon :icon="['fal', 'triangle-exclamation']" class="h-6 w-6 text-red-500 shrink-0" />
                </div>
                <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Favoriet verwijderen
                  </DialogTitle>
                  <div class="mt-2">
                    <p class="text-sm text-gray-500">Je staat op het punt om favoriet <span
                        v-html="deleteDialogText"></span>
                      te verwijderen. Weet je dit zeker?</p>
                  </div>
                </div>
              </div>
              <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <Link preserve-scroll method="delete" :href="deleteDialogUrl" type="button"
                  class="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                  @click="deleteDialog = false" as="button">Verwijderen</Link>
                <button type="button"
                  class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  @click="deleteDialog = false" ref="cancelButtonRef">Annuleren</button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
  <!-- END DELETE DIALOG -->
</template>
